import React, { useState, useEffect } from 'react';

interface SystemMetrics {
  cpu: {
    usage: number;
    cores: number;
    temperature: number;
  };
  memory: {
    total: number;
    used: number;
    available: number;
    usage: number;
  };
  disk: {
    total: number;
    used: number;
    available: number;
    usage: number;
  };
  network: {
    bytesIn: number;
    bytesOut: number;
    connections: number;
  };
  uptime: number;
  loadAverage: number[];
}

interface ProcessInfo {
  id: number;
  name: string;
  cpu: number;
  memory: number;
  status: string;
  uptime: number;
}

const SystemComponent: React.FC = () => {
  const [systemMetrics, setSystemMetrics] = useState<SystemMetrics | null>(null);
  const [processes, setProcesses] = useState<ProcessInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  useEffect(() => {
    fetchSystemData();
    const interval = setInterval(fetchSystemData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchSystemData = async () => {
    setIsLoading(true);
    try {
      const mockMetrics: SystemMetrics = {
        cpu: { usage: 45.2, cores: 8, temperature: 65 },
        memory: { total: 16384, used: 8192, available: 8192, usage: 50 },
        disk: { total: 512000, used: 256000, available: 256000, usage: 50 },
        network: { bytesIn: 1024000, bytesOut: 512000, connections: 150 },
        uptime: 86400,
        loadAverage: [1.2, 1.5, 1.8]
      };

      const mockProcesses: ProcessInfo[] = [
        { id: 1234, name: 'node', cpu: 15.2, memory: 512, status: 'running', uptime: 3600 },
        { id: 5678, name: 'nginx', cpu: 2.1, memory: 128, status: 'running', uptime: 7200 },
        { id: 9012, name: 'mongodb', cpu: 8.5, memory: 1024, status: 'running', uptime: 1800 }
      ];

      setSystemMetrics(mockMetrics);
      setProcesses(mockProcesses);
    } catch (error) {
      console.error('Error fetching system data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUsageColor = (usage: number) => {
    if (usage >= 90) return 'text-red-600';
    if (usage >= 70) return 'text-brand-red';
    if (usage >= 50) return 'text-brand-blue-light';
    return 'text-scarf-green';
  };

  const getUsageBarColor = (usage: number) => {
    if (usage >= 90) return 'bg-red-500';
    if (usage >= 70) return 'bg-brand-red';
    if (usage >= 50) return 'bg-brand-blue-light';
    return 'bg-scarf-green';
  };

  const formatBytes = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      {/* System Overview */}
      {systemMetrics && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">CPU Usage</h3>
            <div className={`text-3xl font-bold ${getUsageColor(systemMetrics.cpu.usage)}`}>
              {systemMetrics.cpu.usage.toFixed(1)}%
            </div>
            <p className="text-gray-500 text-sm">
              {systemMetrics.cpu.cores} cores, {systemMetrics.cpu.temperature}°C
            </p>
            <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${getUsageBarColor(systemMetrics.cpu.usage)}`}
                style={{ width: `${systemMetrics.cpu.usage}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Memory Usage</h3>
            <div className={`text-3xl font-bold ${getUsageColor(systemMetrics.memory.usage)}`}>
              {systemMetrics.memory.usage.toFixed(1)}%
            </div>
            <p className="text-gray-500 text-sm">
              {formatBytes(systemMetrics.memory.used * 1024 * 1024)} / {formatBytes(systemMetrics.memory.total * 1024 * 1024)}
            </p>
            <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${getUsageBarColor(systemMetrics.memory.usage)}`}
                style={{ width: `${systemMetrics.memory.usage}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Disk Usage</h3>
            <div className={`text-3xl font-bold ${getUsageColor(systemMetrics.disk.usage)}`}>
              {systemMetrics.disk.usage.toFixed(1)}%
            </div>
            <p className="text-gray-500 text-sm">
              {formatBytes(systemMetrics.disk.used * 1024 * 1024)} / {formatBytes(systemMetrics.disk.total * 1024 * 1024)}
            </p>
            <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${getUsageBarColor(systemMetrics.disk.usage)}`}
                style={{ width: `${systemMetrics.disk.usage}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">System Uptime</h3>
            <div className="text-3xl font-bold text-brand-blue-light">
              {formatUptime(systemMetrics.uptime)}
            </div>
            <p className="text-gray-500 text-sm">
              Load: {systemMetrics.loadAverage.map(load => load.toFixed(2)).join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Network Stats */}
      {systemMetrics && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Network Statistics</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-blue-light">
                {formatBytes(systemMetrics.network.bytesIn)}
              </div>
              <p className="text-gray-500 text-sm">Bytes In</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-brand-red">
                {formatBytes(systemMetrics.network.bytesOut)}
              </div>
              <p className="text-gray-500 text-sm">Bytes Out</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-scarf-green">
                {systemMetrics.network.connections}
              </div>
              <p className="text-gray-500 text-sm">Active Connections</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">System Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={fetchSystemData}
            className="bg-brand-red hover:bg-brand-red/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Refresh Metrics
          </button>
          <button
            onClick={() => console.log('Generate system report')}
            className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            System Report
          </button>
          <button
            onClick={() => console.log('Restart services')}
            className="bg-scarf-green hover:bg-scarf-green/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Restart Services
          </button>
        </div>
      </div>

      {/* Process List */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Active Processes</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-gray-800">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-deep-navy font-bold">PID</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Process</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">CPU %</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Memory (MB)</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Uptime</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((process) => (
                <tr key={process.id} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-mono text-sm">{process.id}</td>
                  <td className="py-3 px-4 font-bold">{process.name}</td>
                  <td className="py-3 px-4">
                    <span className={`font-bold ${getUsageColor(process.cpu)}`}>
                      {process.cpu.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4">{process.memory}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 rounded text-xs font-bold bg-scarf-green/20 text-scarf-green">
                      {process.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {formatUptime(process.uptime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">System Health</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-scarf-green font-bold mb-3">Current Status</h4>
            <ul className="text-gray-600 space-y-2">
              <li>CPU: Normal operation</li>
              <li>Memory: Adequate available</li>
              <li>Disk: Sufficient space</li>
              <li>Network: Stable connection</li>
            </ul>
          </div>
          <div>
            <h4 className="text-scarf-green font-bold mb-3">Recommendations</h4>
            <ul className="text-gray-600 space-y-2">
              <li>Monitor CPU usage trends</li>
              <li>Consider memory optimization</li>
              <li>Schedule disk cleanup</li>
              <li>Review network performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemComponent;
